import { Text, Flex, Spinner } from "@radix-ui/themes";
import { useCallback, useMemo } from "react";
import Checkbox from "../checkbox/Checkbox";
import styles from "./Checklist.module.scss";

export interface ChecklistItem {
  id: string;
  name: string;
}

interface ChecklistProps {
  items: ChecklistItem[];
  selectedIds: string[];
  onSelectionChange: (selectedIds: string[]) => void;
  isLoading?: boolean;
  disabled?: boolean;
  title?: string;
  description?: string;
}

function Checklist({
  items,
  selectedIds,
  onSelectionChange,
  isLoading = false,
  disabled = false,
  title,
  description,
}: ChecklistProps) {
  const handleItemToggle = useCallback(
    (itemId: string, isChecked: boolean) => {
      if (disabled) return;

      const newSelectedIds = isChecked
        ? [...selectedIds, itemId]
        : selectedIds.filter((id) => id !== itemId);

      onSelectionChange(newSelectedIds);
    },
    [selectedIds, onSelectionChange, disabled]
  );

  const handleSelectAll = useCallback(() => {
    if (disabled) return;

    const allSelected = selectedIds.length === items.length;
    onSelectionChange(allSelected ? [] : items.map((item) => item.id));
  }, [selectedIds, items, onSelectionChange, disabled]);

  const isAllSelected = useMemo(() => {
    return items.length > 0 && selectedIds.length === items.length;
  }, [selectedIds.length, items.length]);

  const isIndeterminate = useMemo(() => {
    return selectedIds.length > 0 && selectedIds.length < items.length;
  }, [selectedIds.length, items.length]);

  if (isLoading) {
    return (
      <Flex direction="column" gap="3">
        {title && (
          <Text size="3" weight="medium">
            {title}
          </Text>
        )}
        {description && (
          <Text size="2" color="gray">
            {description}
          </Text>
        )}
        <Flex align="center" gap="2">
          <Spinner size="2" />
          <Text size="2" color="gray">
            Loading categories...
          </Text>
        </Flex>
      </Flex>
    );
  }

  if (items.length === 0) {
    return (
      <Flex direction="column" gap="3">
        {title && (
          <Text size="3" weight="medium">
            {title}
          </Text>
        )}
        {description && (
          <Text size="2" color="gray">
            {description}
          </Text>
        )}
        <Text size="2" color="gray">
          No categories available
        </Text>
      </Flex>
    );
  }

  return (
    <Flex direction="column" gap="3">
      {title && (
        <Text size="3" weight="medium">
          {title}
        </Text>
      )}
      {description && (
        <Text size="2" color="gray">
          {description}
        </Text>
      )}

      {/* Select All checkbox */}
      <div className={styles.SelectAllContainer}>
        <Checkbox
          label="Select All"
          value={isAllSelected}
          onChange={handleSelectAll}
          disabled={disabled}
        />
        {isIndeterminate && (
          <Text size="1" color="gray" className={styles.SelectionCount}>
            ({selectedIds.length} of {items.length} selected)
          </Text>
        )}
      </div>

      {/* Individual category checkboxes */}
      <Flex direction="column" gap="2" className={styles.ItemsContainer}>
        {items.map((item) => (
          <Checkbox
            key={item.id}
            label={item.name}
            value={selectedIds.includes(item.id)}
            onChange={(checked) => handleItemToggle(item.id, checked)}
            disabled={disabled}
          />
        ))}
      </Flex>
    </Flex>
  );
}

export default Checklist;
